/**
 * RaceWaitingRoom Component
 *
 * Waiting room display showing join code and participants.
 * Allows host to start the race once participants have joined.
 */

import { useState } from 'react';
import type { RaceRoom, RaceParticipant } from '@/types/race';
import { JoinCodeDisplay } from './JoinCodeDisplay';
import { ParticipantList } from './ParticipantList';
import styles from './Race.module.css';

interface SimulatorProps {
  simulatedCount: number;
  isAddingBots: boolean;
  addSimulatedParticipants: (count: number) => Promise<{ success: boolean; error?: string }>;
  cleanupSimulation: () => Promise<void>;
}

interface RaceWaitingRoomProps {
  room: RaceRoom;
  participants: RaceParticipant[];
  onStartRace: () => void;
  onCancel: () => void;
  simulatorProps?: SimulatorProps;
}

export function RaceWaitingRoom({
  room,
  participants,
  onStartRace,
  onCancel,
  simulatorProps,
}: RaceWaitingRoomProps) {
  const canStart = participants.length > 0;
  const [showSimulator, setShowSimulator] = useState(false);
  const [botCount, setBotCount] = useState(5);

  const handleAddBots = async () => {
    if (simulatorProps) {
      await simulatorProps.addSimulatedParticipants(botCount);
    }
  };

  return (
    <div className={styles.waitingRoomContainer}>
      <JoinCodeDisplay code={room.join_code} />

      <div className={styles.roomConfig}>
        <div className={styles.configItem}>
          <span className={styles.configLabel}>Questions</span>
          <span className={styles.configValue}>{room.question_count}</span>
        </div>
        <div className={styles.configItem}>
          <span className={styles.configLabel}>Apps</span>
          <span className={styles.configValue}>
            {room.allowed_apps.map((app) => app.charAt(0).toUpperCase() + app.slice(1)).join(', ')}
          </span>
        </div>
      </div>

      <ParticipantList
        participants={participants}
        maxParticipants={room.max_participants}
      />

      <div className={styles.waitingRoomActions}>
        <button
          onClick={onStartRace}
          className={styles.primaryButton}
          disabled={!canStart}
        >
          {canStart ? 'Start Race' : 'Waiting for participants...'}
        </button>
        <button
          onClick={onCancel}
          className={styles.cancelRaceButton}
        >
          Cancel Race
        </button>
      </div>

      {/* Demo Simulator Toggle */}
      {simulatorProps && (
        <div className={styles.simulatorSection}>
          <button
            onClick={() => setShowSimulator(!showSimulator)}
            className={styles.simulatorToggle}
          >
            {showSimulator ? 'Hide Demo Mode' : 'Demo Mode'}
          </button>

          {showSimulator && (
            <div className={styles.simulatorPanel}>
              <p className={styles.simulatorHint}>
                Add simulated participants for demo purposes. They will automatically race when you start.
              </p>
              <div className={styles.simulatorControls}>
                <label className={styles.simulatorLabel}>
                  Number of bots:
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={botCount}
                    onChange={(e) => setBotCount(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                    className={styles.simulatorInput}
                  />
                </label>
                <button
                  onClick={handleAddBots}
                  disabled={simulatorProps.isAddingBots}
                  className={styles.simulatorButton}
                >
                  {simulatorProps.isAddingBots ? 'Adding...' : `Add ${botCount} Bots`}
                </button>
                {simulatorProps.simulatedCount > 0 && (
                  <button
                    onClick={simulatorProps.cleanupSimulation}
                    className={styles.simulatorButtonDanger}
                  >
                    Remove All Bots ({simulatorProps.simulatedCount})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
